import type {
  PiniaPlugin,
  PiniaPluginContext,
  StateTree,
  Store,
  SubscriptionCallbackMutation,
} from 'pinia'

import type {
  Serializer,
  StorageLike,
} from '@packages/persiststate/types/storage.ts'
import type {
  PersistedStateOptions,
} from '@packages/persiststate/types/store-persist.ts'
import { normalizeOptions } from '@packages/persiststate/utils/normalize.ts'
import { pick } from '@packages/persiststate/utils/pick.ts'
import { getPersistedStateKey } from '@packages/persiststate/utils/key.ts'

interface Persistence {
  storage?: StorageLike
  serializer: Serializer
  paths?: string[]
  key: string
  beforeRestore?: (c: PiniaPluginContext) => void
  afterRestore?: (c: PiniaPluginContext) => void
}

const parsePersistence = (store: Store) => {
  return (storePersistOptions: PersistedStateOptions): Persistence | null => {
    try {
      const {
        storage = localStorage,
        beforeRestore = undefined,
        afterRestore = undefined,
        key,
        paths,
        appendPathsToKey = false,
      } = storePersistOptions

      const serializer = {
        serialize: (
          storePersistOptions.serializer?.serialize ||
          storePersistOptions.storage?.serialize ||
          JSON.stringify
        ),
        deserialize: (
          storePersistOptions.serializer?.deserialize ||
          storePersistOptions.storage?.deserialize ||
          JSON.parse
        ),
      }

      const uniqueKey = getPersistedStateKey(store.$id, { key, paths, appendPathsToKey })

      return {
        storage,
        beforeRestore,
        afterRestore,
        serializer,
        key: uniqueKey,
        paths,
      }
    } catch (e) {
      return null
    }
  }
}

const hydrateStore = (
  store: Store,
  { storage, serializer, key, paths }: Persistence
) => {
  try {
    const fromStorage = storage?.getItem(
      key,
      {
        state: store.$state,
        paths,
        store,
      }
    )

    if (fromStorage) {
      store.$patch(serializer?.deserialize(fromStorage))
    }
  } catch (e) {
    //
  }
}

const persistState = (
  state: StateTree,
  { storage, serializer, key, paths }: Persistence,
  store: Store
) => {
  try {
    const toStore = Array.isArray(paths)
      ? pick(state, paths)
      : state

    storage?.setItem(
      key,
      serializer!.serialize(toStore as StateTree),
      {
        state,
        paths,
        store,
      }
    )
  } catch (e) {
    //
  }
}

const persistedStatePlugin: PiniaPlugin = (context: PiniaPluginContext) => {
  const {
    options: { persist },
    store,
    pinia,
  } = context

  if (!persist) {
    return
  }

  // HMR handling, ignores stores created as "hot" stores
  if (!(store.$id in pinia.state.value)) {
    // @ts-expect-error `_s is a stripped @internal`
    const original_store = pinia._s.get(store.$id.replace('__hot:', ''))
    if (original_store) {
      Promise.resolve().then(() => original_store.$persist())
    }
    return
  }

  const persistences = (
      Array.isArray(persist)
        ? persist.map(p => normalizeOptions(p))
        : [normalizeOptions(persist)]
    ).map(parsePersistence(store)).filter(Boolean) as Persistence[]

  store.$persist = () => {
    persistences.forEach((persistence) => {
      persistState(store.$state, persistence, store)
    })
  }

  store.$hydrate = ({ runHooks = true } = {}) => {
    persistences.forEach((persistence) => {
      const { beforeRestore, afterRestore } = persistence

      if (runHooks) {
        beforeRestore?.(context)
      }

      hydrateStore(store, persistence)

      if (runHooks) {
        afterRestore?.(context)
      }
    })
  }

  persistences.forEach((persistence) => {
    const { beforeRestore, afterRestore } = persistence

    beforeRestore?.(context)

    hydrateStore(store, persistence)

    afterRestore?.(context)

    /**
       * Note: $subscribe does not work as expected on SSR. Will only fire on some changes while rendering
       */
    store.$subscribe(
      (
        _mutation: SubscriptionCallbackMutation<StateTree>,
        state: StateTree
      ) => {
        persistState(state, persistence, store)
      },
      { detached: true }
    )
  })
}

export {
  persistedStatePlugin,
}